import { useEffect, useState } from 'react';
import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import { useId, useIsLoading } from '../../page-framework/panel/Panel';
import { PanelEditingActions } from '../../page-framework/panel/PanelActions';
import { MATRIX_PANEL_PREVIEW_IMAGE, PANEL_CATEGORIES } from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import usePanelDataService from '../../../services/PanelDataService';
import QueryMatrix from '../../matrix/QueryMatrix';
import BusinessServiceDetail from './BusinessServiceDetail';
import { useTimeRangeSelection } from '../../charts/status-breakout/UseTimeRangeSelection';
import { FilterStateContainer } from '../../../stores/FilterStateStore';
import { businessServiceFilterId, timeRangeFilterId, useBusinessService } from '../../filters';
import { useDataService } from '../../../services/ApiDataService';
import MatrixDisplayOptionsAction, { useStatusTrendVisibility } from '../../matrix/internal/MatrixDisplayOptionsAction';

const title = t`Business Service Tests by nPoint`;

const countsSource = {
    url: '/query/table',
    params: {
        type: 'businessservice',
        metrics: [
            'status',
            'bsServerCounts',
            'bsDeviceCounts',
            'bsTestCounts',
            'bsTestExperience',
            'bsStatusServers',
            'bsStatusDevices',
        ],
        renderAsEntity: 'true',
        trends: ['status', 'bsDeviceCounts', 'bsServerCounts', 'bsTestCounts'],
    },
};

function BusinessServiceDetailPanel({ flexHeight }) {
    const id = useId();
    const [, /* Not used */ setIsLoading] = useIsLoading();
    const [timeRangeSelection, setTimeRangeSelection] = useState(undefined);
    const showStatusTrend = useStatusTrendVisibility(setTimeRangeSelection);
    const matrixFilterState = useTimeRangeSelection(timeRangeSelection, showStatusTrend);
    const bs = useBusinessService();
    const showMatrix = bs?.hasTests || false;
    const counts = usePanelDataService(countsSource);
    const dataService = useDataService();

    useEffect(() => {
        // The status trend will set the panel's loading flag when it loads data.  We need to be sure that
        // the loading flag gets cleared when the status trend is hidden.  Otherwise, the loading indicator
        // will be displayed indefinitely if the status trend was in the process of loading data when it
        // was hidden.
        if (!showStatusTrend) setIsLoading(false);
    }, [showStatusTrend, setIsLoading]);

    if (!counts) return null;

    return (
        <>
            <PanelEditingActions>
                <MatrixDisplayOptionsAction />
            </PanelEditingActions>
            {showStatusTrend && (
                <BusinessServiceDetail statusCounts={counts} onTimeRangeChange={setTimeRangeSelection} />
            )}
            {showMatrix && matrixFilterState && (
                <FilterStateContainer
                    name={`${id}-matrix-filter-state`}
                    dataService={dataService}
                    manageUrl={false}
                    state={matrixFilterState}
                >
                    <QueryMatrix visibleRows={flexHeight ? 0 : 10} pivot="bsWorstTestCounts" />
                </FilterStateContainer>
            )}
        </>
    );
}

BusinessServiceDetailPanel.propTypes = panelPropTypes;

BusinessServiceDetailPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    autoHeight: true,
};

export default Panel({
    typeAlias: 'BusinessServiceMatrix',
    catalogEntry: {
        category: PANEL_CATEGORIES.BUSINESS_SERVICE,
        title,
        description: t`Status matrix for service tests by nPoints in a selected business service`,
        previewImage: MATRIX_PANEL_PREVIEW_IMAGE,
    },
    filters: {
        required: [timeRangeFilterId, businessServiceFilterId],
        optional: [],
    },
})(BusinessServiceDetailPanel);
